import React, { useCallback, useRef, useState } from "react"

import { ArrowDown, ArrowUp, Loader2 } from "lucide-react"
import { useNavigate } from "react-router-dom"

import { formatDate } from "@/components/ui/formatDate"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { allReasons } from "@/config/stageDialogConfig"
import { useApplicationsByStage } from "@/hooks/useApplicationsByStage"
import { FilterState } from "@/types/application"

import { stagesConfig } from "./job-postings/KanbanStageConfig"

interface ApplicationTableWithInfiniteScrollProps {
  stage?: string
  searchQuery?: string
  filters?: Partial<FilterState>
}

type SortField = "name" | "location" | "jobPosting" | "stage" | "submittedDate" | "application_id"
type SortDirection = "asc" | "desc"

const ApplicationTableWithInfiniteScroll: React.FC<ApplicationTableWithInfiniteScrollProps> = ({
  stage = "all",
  searchQuery = "",
  filters = {},
}) => {
  const navigate = useNavigate()
  const [sortField, setSortField] = useState<SortField>("submittedDate")
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc")

  const { applications, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } =
    useApplicationsByStage({
      stage,
      limit: 20,
      searchQuery,
      filters,
    })

  const observer = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading || isFetchingNextPage) return

      if (observer.current) observer.current.disconnect()

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage()
        }
      })

      if (node) observer.current.observe(node)
    },
    [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage],
  )

  const handleRowClick = (applicationId: string) => {
    navigate(`/applications/${applicationId}`)
  }

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return null
    return sortDirection === "asc" ? (
      <ArrowUp className="h-4 w-4" />
    ) : (
      <ArrowDown className="h-4 w-4" />
    )
  }

  const getStageMeta = (stageId?: string | null) => {
    if (!stageId) return undefined
    return stagesConfig.find((item) => item.id === stageId)
  }

  const getStageLabel = (stageId?: string | null) =>
    getStageMeta(stageId)?.label || stageId?.replace(/-/g, " ") || "N/A"

  const getSegmentLabel = (stageId?: string | null, segmentId?: string | null) => {
    if (!stageId || !segmentId) return null
    const stage = getStageMeta(stageId)
    const segment = stage?.segments?.find((seg) => seg.id === segmentId)
    return segment?.label || segmentId
  }

  const renderStageReason = (stageReason?: string | null) => {
    if (!stageReason) return null
    const reason = allReasons.find((item) => item.value === stageReason)
    return reason?.label || stageReason
  }

  // Sort applications client-side
  const sortedApplications = [...applications].sort((a, b) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let fieldA: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let fieldB: any

    if (sortField === "application_id") {
      fieldA = a.application_id
      fieldB = b.application_id
    } else if (sortField === "stage") {
      fieldA = a.stage
      fieldB = b.stage
    } else {
      fieldA = a[sortField]
      fieldB = b[sortField]
    }

    if (typeof fieldA === "string" && typeof fieldB === "string") {
      return sortDirection === "asc" ? fieldA.localeCompare(fieldB) : fieldB.localeCompare(fieldA)
    }

    return 0
  })

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead onClick={() => toggleSort("application_id")} className="cursor-pointer">
              <div className="flex items-center">
                Application ID {renderSortIcon("application_id")}
              </div>
            </TableHead>
            <TableHead onClick={() => toggleSort("name")} className="cursor-pointer">
              <div className="flex items-center">Name {renderSortIcon("name")}</div>
            </TableHead>
            <TableHead onClick={() => toggleSort("location")} className="cursor-pointer">
              <div className="flex items-center">Location {renderSortIcon("location")}</div>
            </TableHead>
            <TableHead onClick={() => toggleSort("jobPosting")} className="cursor-pointer">
              <div className="flex items-center">Job Posting {renderSortIcon("jobPosting")}</div>
            </TableHead>
            <TableHead onClick={() => toggleSort("stage")} className="cursor-pointer">
              <div className="flex items-center">Current Stage {renderSortIcon("stage")}</div>
            </TableHead>
            <TableHead onClick={() => toggleSort("submittedDate")} className="cursor-pointer">
              <div className="flex items-center">Submitted {renderSortIcon("submittedDate")}</div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && !applications.length ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-palette-jungleGreen500 mx-auto" />
              </TableCell>
            </TableRow>
          ) : isError ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-6 text-red-500">
                Error loading applications
              </TableCell>
            </TableRow>
          ) : sortedApplications.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-6 text-palette-jungleGreen500">
                No applications found with the selected filters
              </TableCell>
            </TableRow>
          ) : (
            <>
              {sortedApplications.map((application) => {
                const stageMeta = getStageMeta(application.stage)
                const StageIcon = stageMeta?.icon
                const segmentLabel = getSegmentLabel(application.stage, application.segment)

                return (
                  <TableRow
                    key={application.id}
                    className="cursor-pointer hover:bg-palette-jungleGreen000"
                    onClick={() => handleRowClick(application.id)}
                  >
                    <TableCell className="font-mono text-sm">
                      {application.application_id}
                    </TableCell>
                    <TableCell className="font-medium">{application.name}</TableCell>
                    <TableCell>{application.location}</TableCell>
                    <TableCell>{application.jobPosting}</TableCell>
                    <TableCell>
                      {["hold", "rejected"].includes(application.stage) ? (
                        <div
                          className="inline-flex flex-col rounded-sm border overflow-hidden text-xs"
                          style={{ borderColor: stageMeta?.color || "#111827" }}
                        >
                          <div
                            className="flex items-center gap-1 px-2.5 py-1 font-semibold text-white"
                            style={{
                              backgroundColor: stageMeta?.color || "#111827",
                            }}
                          >
                            {StageIcon && <StageIcon className="h-3.5 w-3.5 text-white" />}
                            {getStageLabel(application.stage)}
                          </div>
                          <div className="flex flex-col gap-1 px-2.5 py-2 bg-white text-palette-jungleGreen800">
                            {segmentLabel && (
                              <div className="flex items-center gap-1 text-[11px] text-palette-jungleGreen600">
                                <span className="font-medium">{segmentLabel}</span>
                              </div>
                            )}
                            {application.stageReason && (
                              <div className="text-[11px] text-palette-jungleGreen800">
                                <span className="font-semibold">Reason: </span>
                                <span>{renderStageReason(application.stageReason)}</span>
                              </div>
                            )}
                            <div className="text-[11px] text-palette-jungleGreen700 leading-snug">
                              {application.stageNote || "No note provided"}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="inline-flex items-stretch rounded-sm overflow-hidden text-xs font-semibold border"
                          style={{ borderColor: stageMeta?.color || "#111827" }}
                        >
                          <div
                            className="flex items-center gap-1 px-2.5 py-1 text-white"
                            style={{ backgroundColor: stageMeta?.color || "#111827" }}
                          >
                            {StageIcon && <StageIcon className="h-3.5 w-3.5 text-white" />}
                            {getStageLabel(application.stage)}
                          </div>
                          {segmentLabel && (
                            <div className="flex items-center gap-1 px-2 py-1 bg-white text-palette-jungleGreen700 border-l border-palette-jungleGreen200">
                              <span className="font-medium">{segmentLabel}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(application.submittedDate)}</TableCell>
                  </TableRow>
                )
              })}
              {hasNextPage && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-2">
                    <div ref={loadMoreRef} className="py-2">
                      {isFetchingNextPage && (
                        <Loader2 className="h-4 w-4 animate-spin mx-auto text-palette-jungleGreen500" />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </>
          )}
        </TableBody>
      </Table>
    </div>
  )
}

export default ApplicationTableWithInfiniteScroll
